<template>
  <div>
    <!--自动备份提示横幅-->
    <div class="backup-banner">
      <i class="h-icon-email"></i>
      <span>启用自动备份到邮箱，数据永不丢失</span>
    </div>

    <div class="h-panel-bar">
      <Tabs :datas="tabDatas" v-model="selectedTab"></Tabs>
    </div>

    <!-- 数据备份 -->
    <div v-if="selectedTab === 'backup'">
      <Row type="flex" :space-x="10" class="margin-right-left padding-top">
        <Cell>
          <Button color="primary" @click="doBackup" :loading="loading">开始备份</Button>
          <Button @click="uploadLocal">上传本地备份</Button>
          <Button icon="fa fa-refresh" @click="loadList">刷新</Button>
          <input type="file" ref="backupFile" accept=".sql,.zip" style="display:none" @change="fileChange"/>
        </Cell>
        <Cell flex>
          <div class="backup-right-info">
            <span class="backup-space">已使用空间 {{ formatSize(totalSize) }}</span>
            <span class="backup-setting" @click="openAutoBackupSetting">
              <i class="h-icon-setting"></i> 自动备份设置
            </span>
          </div>
        </Cell>
      </Row>
      <div class="h-panel-body padding-top">
        <Table :datas="backupList" :border="true" :loading="loading">
          <TableItem title="备份名称" prop="name"></TableItem>
          <TableItem title="备份日期" prop="createDate" :width="170"></TableItem>
          <TableItem title="文件大小" :width="130" align="right">
            <template slot-scope="{data}">{{ formatSize(data.fileSize) }}</template>
          </TableItem>
          <TableItem title="操作" :width="180">
            <div class="actions" slot-scope="{data}">
              <span @click="doRestore(data)">恢复</span>
              <a :href="downloadHref(data)" target="_blank">下载</a>
              <span @click="remove(data)">删除</span>
            </div>
          </TableItem>
        </Table>
        <div v-if="backupList.length === 0 && !loading" class="empty-tip">暂无数据</div>
      </div>
    </div>

    <!-- 电子档案 -->
    <div v-if="selectedTab === 'archive'">
      <Row type="flex" :space-x="10" class="margin-right-left padding-top">
        <Cell>
          <Button color="primary" @click="uploadArchive">上传档案</Button>
          <Button icon="fa fa-refresh" @click="loadArchiveList">刷新</Button>
          <input type="file" ref="archiveFile"
                 accept=".pdf,.jpg,.jpeg,.png,.gif,.xml,.xls,.xlsx,.doc,.docx,.zip,.txt"
                 style="display:none" @change="archiveFileChange"/>
        </Cell>
        <Cell flex>
          <div class="backup-right-info">
            <span class="backup-space">已使用空间 {{ formatSize(archiveTotalSize) }}</span>
          </div>
        </Cell>
      </Row>
      <div class="h-panel-body padding-top">
        <Table :datas="archiveList" :border="true" :loading="archiveLoading">
          <TableItem title="文件名称" prop="name"></TableItem>
          <TableItem title="文件来源" :width="120">
            <template slot-scope="{data}">{{ sourceText(data.source) }}</template>
          </TableItem>
          <TableItem title="文件类型" prop="fileType" :width="100"></TableItem>
          <TableItem title="文件大小" :width="120" align="right">
            <template slot-scope="{data}">{{ formatSize(data.fileSize) }}</template>
          </TableItem>
          <TableItem title="上传日期" prop="createDate" :width="170"></TableItem>
          <TableItem title="备注" prop="remark" :width="160"></TableItem>
          <TableItem title="操作" :width="120">
            <div class="actions" slot-scope="{data}">
              <a :href="$api.backup.archiveDownloadUrl(data.id)" target="_blank">下载</a>
              <span @click="removeArchive(data)">删除</span>
            </div>
          </TableItem>
        </Table>
        <div v-if="archiveList.length === 0 && !archiveLoading" class="empty-tip">暂无数据</div>
      </div>
    </div>

    <!-- 归档 -->
    <div v-if="selectedTab === 'filing'">
      <Row type="flex" :space-x="10" class="margin-right-left padding-top">
        <Cell>
          <Button color="primary" @click="doFiling" :loading="filingLoading">开始归档</Button>
          <Button icon="fa fa-refresh" @click="loadFilingList">刷新</Button>
        </Cell>
        <Cell flex>
          <div class="backup-right-info">
            <span class="backup-space">已使用空间 {{ formatSize(archiveTotalSize) }}</span>
          </div>
        </Cell>
      </Row>
      <div class="h-panel-body padding-top">
        <Table :datas="filingList" :border="true" :loading="filingLoading">
          <TableItem title="档案名称" prop="name"></TableItem>
          <TableItem title="归档日期" prop="createDate" :width="170"></TableItem>
          <TableItem title="文件大小" :width="130" align="right">
            <template slot-scope="{data}">{{ formatSize(data.fileSize) }}</template>
          </TableItem>
          <TableItem title="操作" :width="180">
            <div class="actions" slot-scope="{data}">
              <a :href="$api.backup.archiveDownloadUrl(data.id)" target="_blank">下载</a>
              <span @click="removeFiling(data)">删除</span>
            </div>
          </TableItem>
        </Table>
        <div v-if="filingList.length === 0 && !filingLoading" class="empty-tip">暂无数据</div>
      </div>
    </div>

    <!-- 自动备份设置弹窗 -->
    <Modal v-model="showAutoBackupSetting" title="自动备份设置" :width="500">
      <div class="auto-backup-form">
        <div class="form-item">
          <label class="form-label">启用自动备份：</label>
          <HSwitch v-model="autoBackupForm.enabled"></HSwitch>
          <span class="form-tip">关闭后停止每月自动备份</span>
        </div>
        <div class="form-item">
          <label class="form-label">每月备份日期：</label>
          <Select v-model="autoBackupForm.backupDay" :datas="backupDayOptions" :deletable="false" :disabled="!autoBackupForm.enabled"></Select>
        </div>
        <div class="form-item">
          <label class="form-label">备份发送到邮箱：</label>
          <input type="email" v-model="autoBackupForm.email" placeholder="请输入接收备份的邮箱地址" class="h-input" :disabled="!autoBackupForm.enabled"/>
        </div>
        <div class="form-item">
          <label class="form-label">启用私有邮箱服务器：</label>
          <HSwitch v-model="autoBackupForm.privateServer" :disabled="!autoBackupForm.enabled"></HSwitch>
          <span class="form-tip">开启后使用企业自有邮箱服务器发送备份邮件</span>
        </div>
      </div>
      <div slot="footer">
        <Button @click="showAutoBackupSetting = false">取消</Button>
        <Button color="primary" @click="saveAutoBackupSetting" :loading="savingSetting">保存</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
export default {
  name: 'Backup',
  data() {
    return {
      tabDatas: [
        {title: '数据备份', key: 'backup'},
        {title: '电子档案', key: 'archive'},
        {title: '归档', key: 'filing'}
      ],
      selectedTab: 'backup',
      backupList: [],
      archiveList: [],
      filingList: [],
      loading: false,
      archiveLoading: false,
      filingLoading: false,
      totalSize: 0,
      archiveTotalSize: 0,
      showAutoBackupSetting: false,
      savingSetting: false,
      autoBackupForm: {
        enabled: false,
        backupDay: 1,
        email: '',
        privateServer: false
      },
      backupDayOptions: []
    }
  },
  watch: {
    selectedTab(tab) {
      if ((tab === 'archive' || tab === 'filing') && !this.archiveList.length) {
        this.loadArchiveList();
      }
    }
  },
  methods: {
    formatSize(size) {
      let num = Number(size) || 0;
      if (!num) {
        return '0 B';
      }
      if (num < 1024) {
        return num + ' B';
      }
      if (num < 1024 * 1024) {
        return (num / 1024).toFixed(2) + ' K';
      }
      if (num < 1024 * 1024 * 1024) {
        return (num / (1024 * 1024)).toFixed(2) + ' M';
      }
      return (num / (1024 * 1024 * 1024)).toFixed(2) + ' G';
    },
    loadList() {
      this.loading = true;
      this.$api.backup.list().then(({data}) => {
        this.backupList = data.results || data.records || data || [];
        this.totalSize = this.backupList.reduce((sum, item) => sum + (item.fileSize || 0), 0);
      }).catch(() => {
        this.$Message.error('备份记录加载失败');
      }).finally(() => this.loading = false);
    },
    doBackup() {
      this.$Confirm('确认立即开始备份当前账套全部数据？').then(() => {
        this.loading = true;
        this.$api.backup.create().then(() => {
          this.$Message('备份成功~');
          this.loadList();
        }).catch(() => {
          this.$Message.error('备份失败');
        }).finally(() => this.loading = false);
      });
    },
    uploadLocal() {
      this.$refs.backupFile.click();
    },
    fileChange(e) {
      if (this.$refs.backupFile.files.length) {
        let file = this.$refs.backupFile.files[0];
        let name = file.name.toLowerCase();
        if (!name.endsWith('.sql') && !name.endsWith('.zip')) {
          this.$Message.error('仅支持 .sql 或 .zip 格式的备份文件');
          this.$refs.backupFile.value = '';
          return;
        }
        let formData = new FormData();
        formData.append('file', file);
        this.loading = true;
        this.$api.backup.upload(formData).then(() => {
          this.$Message('上传成功~');
          this.loadList();
        }).catch(() => {
          this.$Message.error('上传失败');
        }).finally(() => {
          this.loading = false;
          this.$refs.backupFile.value = '';
        });
      }
    },
    doRestore(data) {
      this.$Confirm(`确认使用备份"${data.name}"恢复数据？恢复后当前账套数据将被覆盖！`).then(() => {
        this.loading = true;
        this.$api.backup.restore(data.id).then(({data: res}) => {
          if (res && res.successCount !== undefined) {
            this.$Message(`恢复完成：成功 ${res.successCount} 条，跳过 ${res.skippedCount} 条`);
          } else {
            this.$Message('数据恢复成功~');
          }
        }).catch(() => {
          this.$Message.error('数据恢复失败');
        }).finally(() => this.loading = false);
      });
    },
    downloadHref(data) {
      return this.$api.backup.downloadUrl(data.id);
    },
    remove(data) {
      this.$Confirm(`确认删除备份"${data.name}"？`).then(() => {
        this.$api.backup.del(data.id).then(() => {
          this.$Message('删除成功~');
          this.loadList();
        }).catch(() => {
          this.$Message.error('删除失败');
        });
      });
    },
    // 电子档案
    sourceText(source) {
      return source === 'FILING' ? '归档生成' : '手工上传';
    },
    uploadArchive() {
      this.$refs.archiveFile.click();
    },
    archiveFileChange() {
      if (!this.$refs.archiveFile.files.length) return;
      let file = this.$refs.archiveFile.files[0];
      if (file.size > 50 * 1024 * 1024) {
        this.$Message.error('单个档案不能超过 50MB');
        this.$refs.archiveFile.value = '';
        return;
      }
      let formData = new FormData();
      formData.append('file', file);
      this.archiveLoading = true;
      this.$api.backup.archiveUpload(formData).then(() => {
        this.$Message('档案上传成功~');
        this.loadArchiveList();
      }).catch(() => {
        this.$Message.error('档案上传失败');
      }).finally(() => {
        this.archiveLoading = false;
        this.$refs.archiveFile.value = '';
      });
    },
    removeArchive(data) {
      this.$Confirm(`确认删除档案"${data.name}"？`).then(() => {
        this.$api.backup.archiveDel(data.id).then(() => {
          this.$Message('删除成功~');
          this.loadArchiveList();
        }).catch(() => {
          this.$Message.error('删除失败');
        });
      });
    },
    loadArchiveList() {
      this.archiveLoading = true;
      this.$api.backup.archiveList({page: 1, pageSize: 1000}).then(({data}) => {
        this.archiveList = data.results || data.records || [];
        this.archiveTotalSize = this.archiveList.reduce((sum, item) => sum + (item.fileSize || 0), 0);
        this.filingList = this.archiveList.filter(item => item.source === 'FILING');
      }).catch(() => {
        this.$Message.error('电子档案加载失败');
      }).finally(() => this.archiveLoading = false);
    },
    // 归档
    doFiling() {
      this.$Confirm('确认开始归档当前账套数据？将生成账套数据快照存入电子档案仓库。').then(() => {
        this.filingLoading = true;
        this.$api.backup.filing().then(() => {
          this.$Message('归档成功~');
          this.loadFilingList();
        }).catch(() => {
          this.$Message.error('归档失败');
        }).finally(() => this.filingLoading = false);
      });
    },
    loadFilingList() {
      this.loadArchiveList();
    },
    removeFiling(data) {
      this.$Confirm(`确认删除归档"${data.name}"？`).then(() => {
        this.$api.backup.archiveDel(data.id).then(() => {
          this.$Message('删除成功~');
          this.loadFilingList();
        }).catch(() => {
          this.$Message.error('删除失败');
        });
      });
    },
    // 自动备份设置
    loadAutoBackupSetting() {
      this.$api.backup.getAutoConfig().then(({data}) => {
        if (data) {
          this.autoBackupForm = {
            enabled: !!data.enabled,
            backupDay: data.backupDay || 1,
            email: data.email || '',
            privateServer: !!data.privateServer
          };
        }
      });
    },
    saveAutoBackupSetting() {
      if (this.autoBackupForm.enabled && !this.autoBackupForm.email) {
        this.$Message.error('启用自动备份时，接收邮箱不能为空');
        return;
      }
      this.savingSetting = true;
      this.$api.backup.saveAutoConfig({
        enabled: this.autoBackupForm.enabled,
        backupDay: this.autoBackupForm.backupDay,
        email: this.autoBackupForm.email,
        privateServer: this.autoBackupForm.privateServer ? 1 : 0
      }).then(({msg}) => {
        this.$Message.success(msg || '自动备份设置保存成功');
        this.showAutoBackupSetting = false;
      }).catch(() => {
        this.$Message.error('保存失败');
      }).finally(() => this.savingSetting = false);
    },
    openAutoBackupSetting() {
      this.loadAutoBackupSetting();
      this.showAutoBackupSetting = true;
    },
    initBackupDayOptions() {
      this.backupDayOptions = [];
      for (let i = 1; i <= 28; i++) {
        this.backupDayOptions.push({title: `每月${i}日`, key: i});
      }
    }
  },
  mounted() {
    this.initBackupDayOptions();
    this.loadList();
  }
}
</script>

<style lang="less" scoped>
.backup-banner {
  margin: 10px;
  padding: 12px 16px;
  background: #e8f4ff;
  border: 1px solid #b6d8ff;
  border-radius: 4px;
  color: #2d8cf0;
  font-size: 14px;

  i {
    margin-right: 8px;
    font-size: 18px;
  }
}

.backup-right-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;

  .backup-space {
    color: #999;
    font-size: 13px;
  }

  .backup-setting {
    color: #2d8cf0;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

.auto-backup-form {
  .form-item {
    margin-bottom: 16px;
    display: flex;
    align-items: center;

    .form-label {
      width: 140px;
      text-align: right;
      margin-right: 10px;
      color: #333;
    }

    .form-tip {
      margin-left: 10px;
      color: #999;
      font-size: 12px;
    }
  }
}

.actions span {
  cursor: pointer;
  color: #2d8cf0;
  margin-right: 8px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
